import { useState } from "react";
import { openMailto } from "@/utils/mailto";
import Help from "@features/business/components/help";
import Button from "@features/business/components/button";
import inspectionImage from "@assets/images/inspection-technician.png";
import developersImage from "@assets/images/developers-authorities.png";
import documentIcon from "@assets/icons/document.svg";
import downloadIcon from "@assets/icons/download-arrow.svg";
import arrowSmallIcon from "@assets/icons/arrow-up-s-fill.svg";
import {
  complianceFrameworkAccordion,
  inspectionAuditPoints,
  complianceDownloads,
  developerAuthorityPoints,
} from "@features/business/data";

interface RegulatoryCompliancePageProps {
  hero: React.ReactNode;
  footer?: React.ReactNode;
}

export default function RegulatoryCompliancePage({
  hero,
  footer,
}: RegulatoryCompliancePageProps) {
  return (
    <div className="w-full flex flex-col bg-white">
      {hero}

      {/* ── COMPLIANCE FRAMEWORK ACCORDION ────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto space-y-3 md:space-y-6">
          {complianceFrameworkAccordion.map((item, index) => (
            <List
              key={index}
              title={item.title}
              index={index + 1}
              isDefaultOpen={index === 0}
            >
              <div className="space-y-5 text-[14px] md:text-sm lg:text-lg text-[#62636C]">
                {item.sections.map((section, si) => {
                  if (section.type === "intro")
                    return <p key={si}>{section.text}</p>;
                  if (section.type === "heading")
                    return (
                      <p key={si} className="font-semibold text-[#090D2C]">
                        {section.label}
                      </p>
                    );
                  if (section.type === "subheading-red")
                    return (
                      <p key={si} className="font-medium text-gis-red">
                        {section.label}
                      </p>
                    );
                  if (section.type === "subheading")
                    return <p key={si}>{section.label}</p>;
                  if (section.type === "list")
                    return (
                      <ul key={si} className="space-y-1.5">
                        {section.items &&
                          section.items.map((it, ii) => (
                            <li key={ii} className="flex items-start gap-3">
                              <span className="flex-shrink-0 mt-1.5 md:mt-3 size-1.5 md:size-3 rounded-full bg-[#62636C]" />
                              <span>{it}</span>
                            </li>
                          ))}
                      </ul>
                    );
                  return null;
                })}
              </div>
            </List>
          ))}
        </div>
      </section>

      {/* ── INSPECTION, AUDITS & QUALITY ASSURANCE ────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col xl:flex-row gap-10 justify-between items-start">
          <div className="xl:max-w-[640px]">
            <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[54px] font-semibold">
              Inspection, Audits &amp; Quality Assurance
            </h2>
            <p className="mt-4 text-[14px] md:text-base lg:text-lg text-[#62636C]">
              To maintain compliance credibility, GASIS performs:
            </p>
            <ul className="mt-5 space-y-3">
              {inspectionAuditPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-1.5 md:mt-3 size-2 md:size-3 rounded-full bg-[#62636C]" />
                  <span className="text-[14px] md:text-base lg:text-lg text-[#62636C]">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[14px] md:text-base lg:text-lg text-[#62636C]">
              All results are documented and maintained for regulatory review.
            </p>
          </div>

          <div className="md:max-w-[500px] w-full flex-shrink-0">
            <img
              src={inspectionImage}
              alt="inspection technician"
              className="w-full rounded-[12px] md:rounded-[24px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE DOWNLOADS ───────────────────────────────────────── */}
      <section className="py-10 md:py-16 px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Compliance Downloads
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-4">
            Customers, developers, regulators, and partners may request the
            following documents:
          </p>

          <div className="grid gap-4 mt-10 lg:grid-cols-2 md:mt-16">
            {complianceDownloads.map((resource, index) => (
              <article
                key={index}
                className="bg-white p-4 md:p-6 rounded-[12px] md:rounded-[24px] flex items-center justify-between gap-4"
              >
                <div className="flex flex-row items-center gap-4 xl:gap-8">
                  <div className="bg-[#F1F1F1] p-2 rounded-[12px] flex-shrink-0">
                    <img
                      src={documentIcon}
                      alt="document icon"
                      className="size-5 md:size-12"
                    />
                  </div>
                  <h4 className="text-[#1E1F24] font-normal text-xs md:text-base lg:text-xl">
                    {resource}
                  </h4>
                </div>
                <button className="flex-shrink-0 size-8 md:size-10 hover:bg-[#F1F1F1] border border-[#F1F1F1] rounded-full grid place-content-center">
                  <img
                    src={downloadIcon}
                    alt="download icon"
                    className="size-5 md:size-7 invert"
                  />
                </button>
              </article>
            ))}
          </div>

          <div className="flex justify-center mt-10 md:mt-16">
            <Button title="Request Compliance Documents" onClick={() => openMailto({ subject: "Request: Compliance Documents", body: "Hello,\n\nI would like to request compliance documentation.\n\nCompany Name:\nDocuments Needed:\n" })} />
          </div>
        </div>
      </section>

      {/* ── FOR DEVELOPERS & AUTHORITIES ──────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col xl:flex-row gap-10 justify-between items-start xl:items-center">
          <div className="xl:max-w-[560px]">
            <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[54px] font-semibold">
              For Developers &amp; Authorities
            </h2>
            <p className="mt-4 text-[14px] md:text-base lg:text-lg text-[#62636C]">
              GASIS provides full regulatory support for:
            </p>
            <ul className="mt-5 space-y-3">
              {developerAuthorityPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-1.5 md:mt-3 size-2 md:size-3 rounded-full bg-[#62636C]" />
                  <span className="text-[14px] md:text-base lg:text-lg text-[#62636C]">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              title="Contact Our Compliance Team"
              className="mt-6 md:mt-12"
              onClick={() => openMailto({ subject: "Compliance Team Contact", to: "compliance@gasis.ae" })}
            />
          </div>

          <div className="md:max-w-[600px] w-full flex-shrink-0">
            <img
              src={developersImage}
              alt="developers and authorities"
              className="w-full rounded-[12px] md:rounded-[24px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── HELP CTA ───────────────────────────────────────────────────── */}
      <Help />

      {footer}
    </div>
  );
}

type ListType = {
  children: React.ReactNode;
  title: string;
  index: number;
  isDefaultOpen: boolean;
};

const List = ({ children, title, index, isDefaultOpen = false }: ListType) => {
  const [open, setOpen] = useState(isDefaultOpen);

  return (
    <article className="p-3 md:p-6 bg-white rounded-[12px] md:rounded-[24px]">
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between gap-5 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="bg-black size-9 md:size-[56px] grid place-content-center rounded-[12px] flex-shrink-0">
            <span className="text-xs font-semibold text-white md:text-base lg:text-lg">
              {index >= 10 ? index : `0${index}`}
            </span>
          </div>
          <h3 className="text-[#1C1C1C] text-xs md:text-base lg:text-xl font-semibold">
            {title}
          </h3>
        </div>
        <img
          src={arrowSmallIcon}
          alt="arrow icon"
          className={`flex-shrink-0 transition-transform duration-300 ${
            open ? "" : "rotate-180"
          }`}
        />
      </div>

      {/* GRID COLLAPSIBLE */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="md:px-16 border-[#B9BBC6] border-t pt-4 mt-4">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
};
